// @givmo/crm — one of the organization's own contacts, in a panel beside the list.
//
// Opens from the contact's row on All and on Imported. It reads the contact on its own (the one read
// that says WHY a contact may not be emailed), shows it quietly, and, for a person who may change the
// CRM's contacts, edits it in place and deletes it behind a confirm that names the contact and says
// what goes.
//
// "DO NOT EMAIL" IS ONE WAY. The organization may mark a contact do not email, and the panel says
// why any contact isn't emailed. Nothing here clears a stop, the organization's own included: the
// suppression list clears nothing for an organization, and the server refuses it. Should that rule
// change, the undo belongs in `DoNotEmailNotice` and nowhere else.
//
// NO MATCHING, EVER. Nothing here looks up, suggests or links a Givmo donor, even when the address
// equals one: the organization's own records and the feed stay disjoint.
//
// TERMS GATE: an edit needs the organization's operative terms (the server's 409, with the host's
// action link). Marking do not email and deleting are never blocked by it, which is why a save sends
// only what changed: the mark on its own takes the server's exempt path.

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  ErrorMessage,
  Field,
  Icon,
  IconButton,
  Sheet,
  Skeleton,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { isGivmoError } from '@givmo/api-client';
import { X } from 'lucide-react';
import type { CrmModuleConfig } from './crm-config';
import type { CrmImportedContactOut, CrmImportedContactUpdateRequest } from './wire-types';
import { crmImportedContactOptions, crmKeys } from './crm-queries';
import { deleteCrmImportedContact, updateCrmImportedContact } from './crm-transport';
import {
  decodeCrmImportedContact,
  displayDate,
  isTermsRequired,
  serverMessage,
} from './crm-decode';
import { parseTags } from './imported-contact-dialog';
import { ImportedGiftHistory } from './imported-gift-history';

const PREFIX = 'crm-contact-sheet';

/** The backend's own bounds, so a length is never refused after the fact. */
const MAX = { name: 200, email: 320, phone: 40, address: 500, tag: 50, notes: 4000 } as const;

/** What the panel calls the contact: its name, else its address, else plain words. */
export function contactTitle(contact: CrmImportedContactOut | null | undefined): string {
  const name = contact?.display_name?.trim();
  if (name) return name;
  const email = contact?.email?.trim();
  if (email) return email;
  return 'This contact';
}

/** Why this contact isn't emailed, in one sentence, with the day it was recorded where served. */
export function doNotEmailLine(contact: CrmImportedContactOut): string {
  const on = contact.do_not_email_since ? ` on ${displayDate(contact.do_not_email_since)}` : '';
  switch (contact.do_not_email_reason) {
    case 'unsubscribed':
      return `Unsubscribed${on}. Only they can choose to get your emails again.`;
    case 'spam_report':
      return `Reported your email as spam${on}. Your emails won’t go to them again.`;
    case 'deceased':
      return `Marked deceased in your records${on}. Your emails won’t go to this address.`;
    case 'bounced_hard':
      return `Email to this address bounced${on}, so nothing more is sent to it.`;
    case 'imported_unsubscribe':
      return `Your import said they unsubscribed${on ? ` (recorded${on})` : ''}. Only they can choose to get your emails again.`;
    case 'do_not_contact':
      return `Your import marked them do not contact${on}.`;
    case 'org_marked':
      return `Do not email: set by your team${on}.`;
    default:
      return 'Your organization’s emails don’t go to this contact.';
  }
}

/** Why this contact isn't emailed, above its details. */
function DoNotEmailNotice({ contact }: { contact: CrmImportedContactOut }) {
  return (
    <p
      className="rounded-md border border-border-subtle bg-surface-sunken px-16 py-12 font-sans text-s13 leading-h15 tracking-givmo text-text-strong"
      {...testIdProp(`${PREFIX}-do-not-email`)}
    >
      {doNotEmailLine(contact)}
    </p>
  );
}

/** A refusal in plain words: the server's own where it gave one, ours where it gave none. */
function refusal(error: unknown, action: 'save' | 'delete'): string {
  if (isGivmoError(error) && error.status === 403) {
    return action === 'save'
      ? 'You don’t have permission to change contacts. Ask an admin for access to the CRM.'
      : 'You don’t have permission to delete contacts. Ask an admin for access to the CRM.';
  }
  if (isGivmoError(error) && error.status === 422) {
    return 'Some of these details aren’t valid. Check them and try again.';
  }
  return serverMessage(
    error,
    action === 'save'
      ? 'We couldn’t save this contact. Please try again.'
      : 'We couldn’t delete this contact. Please try again.',
  );
}

interface Draft {
  displayName: string;
  email: string;
  phone: string;
  postalAddress: string;
  tags: string[];
  tagEntry: string;
  notes: string;
  doNotEmail: boolean;
}

function draftFrom(contact: CrmImportedContactOut): Draft {
  return {
    displayName: contact.display_name ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    postalAddress: contact.postal_address ?? '',
    tags: [...(contact.tags ?? [])],
    tagEntry: '',
    notes: contact.notes ?? '',
    doNotEmail: contact.do_not_email === true,
  };
}

function optional(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** What this save changes, and nothing else. */
function changedFields(
  contact: CrmImportedContactOut,
  draft: Draft,
  tags: string[],
): CrmImportedContactUpdateRequest {
  const saved = draftFrom(contact);
  const body: CrmImportedContactUpdateRequest = {};
  const texts = [
    ['display_name', draft.displayName, saved.displayName],
    ['email', draft.email, saved.email],
    ['phone', draft.phone, saved.phone],
    ['postal_address', draft.postalAddress, saved.postalAddress],
    ['notes', draft.notes, saved.notes],
  ] as const;
  for (const [field, now, before] of texts) {
    if (optional(now) !== optional(before)) body[field] = optional(now);
  }
  if (tags.join('\n') !== saved.tags.join('\n')) body.tags = tags;
  return body;
}

export interface ContactSheetProps {
  config: CrmModuleConfig;
  /** The contact the panel is open on, or null when closed. */
  contactId: string | null;
  onClose: () => void;
  /** A save, a mark or a delete changed what the lists and counts show. */
  onChanged: () => void;
}

export function ContactSheet({ config, contactId, onClose, onChanged }: ContactSheetProps) {
  const queryClient = useQueryClient();
  const canEdit = config.canEdit !== false;
  const read = useQuery({
    ...crmImportedContactOptions(config.orgRef, contactId ?? ''),
    enabled: contactId !== null,
  });
  const contact = read.data ?? null;

  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // A different contact (or the panel closing) starts in reading mode.
  useEffect(() => {
    setEditing(false);
    setDeleteOpen(false);
  }, [contactId]);

  const settle = (updated: unknown) => {
    const decoded = decodeCrmImportedContact(updated);
    if (decoded !== null && contactId !== null) {
      queryClient.setQueryData(crmKeys.importedContact(config.orgRef, contactId), updated);
    }
    onChanged();
  };

  const remove = async () => {
    if (contactId === null) return;
    setBusy(true);
    try {
      await deleteCrmImportedContact(config.orgRef, contactId);
      toast.success('Contact deleted');
      setDeleteOpen(false);
      onClose();
      onChanged();
    } catch (error) {
      toast.error(refusal(error, 'delete'));
    } finally {
      setBusy(false);
    }
  };

  const title = contactTitle(contact);
  const subline = contact?.display_name?.trim() ? contact.email : null;

  return (
    <Sheet.Root
      open={contactId !== null}
      // A stray click on the page never throws away an edit in progress.
      disablePointerDismissal={editing}
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <Sheet.Content
        size="md"
        testId={PREFIX}
        initialFocus={false}
        aria-label={contact ? undefined : 'Contact'}
      >
        <Sheet.Header testId={`${PREFIX}-header`}>
          <div className="flex min-w-0 flex-col gap-4">
            <Sheet.Title className="truncate font-sans text-s18 font-w700 tracking-givmo text-text-strong">
              {read.isPending ? 'Contact' : title}
            </Sheet.Title>
            {subline ? (
              <Sheet.Description className="truncate font-sans text-s13 tracking-givmo text-text-secondary">
                {subline}
              </Sheet.Description>
            ) : null}
          </div>
        </Sheet.Header>

        {read.isPending ? (
          <Sheet.Body testId={`${PREFIX}-loading`}>
            <div className="flex flex-col gap-12">
              <Skeleton className="h-16 w-1/2" />
              <Skeleton className="h-16 w-2/3" />
              <Skeleton className="h-16 w-1/3" />
            </div>
          </Sheet.Body>
        ) : contact === null ? (
          <Sheet.Body testId={`${PREFIX}-error`}>
            <p className="font-sans text-s14 leading-h15 tracking-givmo text-text-secondary">
              {read.isError && isGivmoError(read.error) && read.error.status === 400
                ? 'This contact isn’t here anymore. It may have been deleted.'
                : 'We couldn’t load this contact. Please try again.'}
            </p>
          </Sheet.Body>
        ) : editing ? (
          <ContactEditForm
            config={config}
            contact={contact}
            onCancel={() => setEditing(false)}
            onSaved={(updated) => {
              settle(updated);
              setEditing(false);
            }}
          />
        ) : (
          <>
            <Sheet.Body className="flex flex-col gap-24" testId={`${PREFIX}-body`}>
              {contact.do_not_email ? <DoNotEmailNotice contact={contact} /> : null}

              <ContactDetails config={config} contact={contact} />

              {contact.notes ? (
                <section className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-notes`)}>
                  <h3 className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                    Notes
                  </h3>
                  <p className="whitespace-pre-line font-sans text-s13 leading-h15 tracking-givmo text-text-secondary">
                    {contact.notes}
                  </p>
                </section>
              ) : null}

              <ImportedGiftHistory contact={contact} testId={`${PREFIX}-gift-history`} />
            </Sheet.Body>

            {canEdit ? (
              <Sheet.Footer className="justify-between" testId={`${PREFIX}-footer`}>
                <Button
                  appearance="saas"
                  tone="ghost"
                  size="md"
                  className="-ml-14 text-danger-700 hover:text-danger-700"
                  onClick={() => setDeleteOpen(true)}
                  testId={`${PREFIX}-delete`}
                >
                  Delete contact
                </Button>
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="md"
                  onClick={() => setEditing(true)}
                  testId={`${PREFIX}-edit`}
                >
                  Edit
                </Button>
              </Sheet.Footer>
            ) : null}
          </>
        )}

        <Dialog.Root open={deleteOpen} onOpenChange={(open) => !busy && setDeleteOpen(open)}>
          <Dialog.Content
            aria-label={`Delete ${title}`}
            className="flex w-[440px] flex-col gap-12"
            testId={`${PREFIX}-delete-dialog`}
          >
            <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
              Delete {title}?
            </Dialog.Title>
            <p className="font-sans text-s14 leading-h15 tracking-givmo text-text-secondary">
              Their details, tags, notes and imported giving history are removed from your CRM. This
              can’t be undone.
            </p>
            <p className="font-sans text-s14 leading-h15 tracking-givmo text-text-secondary">
              {contact?.do_not_email
                ? 'Their address stays on your do-not-email list, so a later import won’t make them emailable. Emails you’ve already sent aren’t affected.'
                : 'Emails you’ve already sent aren’t affected.'}
            </p>
            <div className="mt-8 flex items-center justify-end gap-8">
              <Dialog.Close
                disabled={busy}
                render={<Button appearance="saas" tone="secondary" size="md" type="button" />}
                {...testIdProp(`${PREFIX}-delete-cancel`)}
              >
                Cancel
              </Dialog.Close>
              <Button
                appearance="saas"
                tone="danger"
                size="md"
                loading={busy}
                onClick={() => void remove()}
                testId={`${PREFIX}-delete-confirm`}
              >
                Delete contact
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Root>
      </Sheet.Content>
    </Sheet.Root>
  );
}

function ContactDetails({
  config,
  contact,
}: {
  config: CrmModuleConfig;
  contact: CrmImportedContactOut;
}) {
  const none = <span className="text-text-tertiary">—</span>;
  const tags = contact.tags ?? [];
  const rows: [string, React.ReactNode][] = [
    ['Phone', contact.phone ? <span className="tabular-nums">{contact.phone}</span> : none],
    [
      'Address',
      contact.postal_address ? (
        <span className="whitespace-pre-line">{contact.postal_address}</span>
      ) : (
        none
      ),
    ],
    [
      'Tags',
      tags.length === 0 ? (
        none
      ) : (
        <span className="flex flex-wrap gap-6">
          {tags.map((tag) => (
            <Badge key={tag} tone="neutral" size="sm">
              {tag}
            </Badge>
          ))}
        </span>
      ),
    ],
    ['Source', config.vocab.suppliedSourceLabel],
    ['Added', contact.created_at ? displayDate(contact.created_at) : none],
  ];
  return (
    <dl
      className="grid grid-cols-[112px_1fr] gap-x-16 gap-y-12 font-sans text-s13 tracking-givmo"
      {...testIdProp(`${PREFIX}-details`)}
    >
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-text-tertiary">{label}</dt>
          <dd className="min-w-0 text-text-strong">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ContactEditForm({
  config,
  contact,
  onCancel,
  onSaved,
}: {
  config: CrmModuleConfig;
  contact: CrmImportedContactOut;
  onCancel: () => void;
  onSaved: (updated: unknown) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(contact));
  const [formError, setFormError] = useState<string | null>(null);
  const [termsRequired, setTermsRequired] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  // One way: once saved, the box is the contact's record of why, and it no longer changes.
  const alreadyStopped = contact.do_not_email === true;
  const hasAddress = draft.email.trim().length > 0;
  const boxDisabled = alreadyStopped || !hasAddress;
  const boxHelp = alreadyStopped
    ? doNotEmailLine(contact)
    : !hasAddress
      ? 'Add an email address to use this.'
      : draft.doNotEmail
        ? 'They won’t get your organization’s emails. Once saved, this can’t be turned off here.'
        : 'Stop your organization’s emails to this contact.';

  const addTags = () => {
    const added = parseTags(draft.tagEntry);
    if (added.length === 0) return;
    const seen = new Set(draft.tags.map((tag) => tag.toLowerCase()));
    const merged = [...draft.tags];
    for (const tag of added) {
      if (!seen.has(tag.toLowerCase())) {
        seen.add(tag.toLowerCase());
        merged.push(tag);
      }
    }
    setDraft((current) => ({ ...current, tags: merged, tagEntry: '' }));
  };

  const submit = async () => {
    if (draft.displayName.trim().length === 0 && draft.email.trim().length === 0) {
      setFormError('Add a name or an email so you can find this contact later.');
      return;
    }
    setFormError(null);
    setTermsRequired(false);
    // A tag still in the box counts, as it would on a form that saves it.
    const pending = parseTags(draft.tagEntry);
    const tags = parseTags([...draft.tags, ...pending].join(','));
    const body: CrmImportedContactUpdateRequest = {
      ...changedFields(contact, draft, tags),
      ...(!alreadyStopped && draft.doNotEmail ? { do_not_email: true as const } : {}),
    };
    if (Object.keys(body).length === 0) {
      onCancel();
      return;
    }
    setSaving(true);
    try {
      const updated = await updateCrmImportedContact(config.orgRef, contact.id, body);
      toast.success('Contact updated');
      onSaved(updated);
    } catch (error) {
      setTermsRequired(isTermsRequired(error));
      setFormError(refusal(error, 'save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Sheet.Body testId={`${PREFIX}-form-body`}>
        <form
          id={`${PREFIX}-form`}
          className="flex flex-col gap-14"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          {...testIdProp(`${PREFIX}-form`)}
        >
          <Field
            label="Name"
            value={draft.displayName}
            maxLength={MAX.name}
            onChange={(event) => set('displayName', event.target.value)}
            autoComplete="off"
            testId={`${PREFIX}-name`}
          />
          <Field
            label="Email"
            type="email"
            value={draft.email}
            maxLength={MAX.email}
            onChange={(event) => set('email', event.target.value)}
            autoComplete="off"
            testId={`${PREFIX}-email`}
          />
          <Field
            label="Phone"
            value={draft.phone}
            maxLength={MAX.phone}
            onChange={(event) => set('phone', event.target.value)}
            autoComplete="off"
            testId={`${PREFIX}-phone`}
          />
          <Textarea
            label="Address"
            rows={2}
            value={draft.postalAddress}
            maxLength={MAX.address}
            onChange={(event) => set('postalAddress', event.target.value)}
            testId={`${PREFIX}-address`}
          />

          <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-tags`)}>
            <span className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary">
              Tags
            </span>
            {draft.tags.length > 0 ? (
              <span className="flex flex-wrap items-center gap-6">
                {draft.tags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-2">
                    <Badge tone="neutral" size="md">
                      {tag}
                    </Badge>
                    <IconButton
                      aria-label={`Remove tag ${tag}`}
                      tone="ghost"
                      size="sm"
                      onClick={() =>
                        set(
                          'tags',
                          draft.tags.filter((other) => other !== tag),
                        )
                      }
                      testId={`${PREFIX}-tag-remove-${tag}`}
                    >
                      <Icon icon={X} size={14} aria-hidden />
                    </IconButton>
                  </span>
                ))}
              </span>
            ) : null}
            <Field
              aria-label="Add a tag"
              placeholder="Add a tag, then press Enter"
              value={draft.tagEntry}
              maxLength={MAX.tag}
              onChange={(event) => set('tagEntry', event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ',') {
                  event.preventDefault();
                  addTags();
                }
              }}
              onBlur={addTags}
              autoComplete="off"
              testId={`${PREFIX}-tag-add`}
            />
          </div>

          <Textarea
            label="Notes"
            rows={3}
            value={draft.notes}
            maxLength={MAX.notes}
            onChange={(event) => set('notes', event.target.value)}
            testId={`${PREFIX}-notes-field`}
          />

          <label
            className="flex items-start gap-10 rounded-md border border-border-subtle px-14 py-12"
            {...testIdProp(`${PREFIX}-do-not-email-row`)}
          >
            <Checkbox
              appearance="saas"
              className="mt-2"
              checked={draft.doNotEmail}
              disabled={boxDisabled}
              onCheckedChange={(checked) => set('doNotEmail', checked)}
              testId={`${PREFIX}-do-not-email-box`}
            />
            <span className="flex flex-col gap-2">
              <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                Do not email
              </span>
              <span
                className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${PREFIX}-do-not-email-help`)}
              >
                {boxHelp}
              </span>
            </span>
          </label>

          {formError !== null ? (
            <div className="flex flex-col gap-6" {...testIdProp(`${PREFIX}-error-message`)}>
              <ErrorMessage>{formError}</ErrorMessage>
              {termsRequired && config.links.terms !== undefined ? (
                <Link
                  to={config.links.terms}
                  className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 underline underline-offset-2"
                  {...testIdProp(`${PREFIX}-terms-link`)}
                >
                  {config.vocab.termsActionLabel}
                </Link>
              ) : null}
            </div>
          ) : null}
        </form>
      </Sheet.Body>
      <Sheet.Footer testId={`${PREFIX}-form-footer`}>
        <Button
          appearance="saas"
          tone="secondary"
          size="md"
          type="button"
          disabled={saving}
          onClick={onCancel}
          testId={`${PREFIX}-cancel`}
        >
          Cancel
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          size="md"
          type="submit"
          form={`${PREFIX}-form`}
          loading={saving}
          testId={`${PREFIX}-save`}
        >
          Save changes
        </Button>
      </Sheet.Footer>
    </>
  );
}
